import React from 'react';
import { Text } from "@geist-ui/core";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import { sanitizeMarkdown, hasMarkdown } from '../../../utils/markdownUtils';
import { useGlossary } from '../../../hooks/useGlossary';
import GlossaryTerm from '../../GlossaryTerm';

const StoryContainer = ({ currentScene, fontSize, isTwoColumns, handleLinkClick }) => {
  const { glossaryTerms, isLoading } = useGlossary();
  console.log('StoryContainer - glossaryTerms:', glossaryTerms, 'isLoading:', isLoading);

  const renderTextWithLinksAndMarkdown = (text) => {
    if (!text) return text;

    // First process "go to" links
    const parts = text.split(/(go to \d+)/gi);
    return parts.map((part, index) => {
      const match = part.match(/go to (\d+)/i);
      if (match) {
        const targetSceneId = match[1];
        return (
          <a
            key={index}
            href="#"
            className="scene-link"
            onClick={e => handleLinkClick(e, targetSceneId)}
          >
            <Text span>{part}</Text>
          </a>
        );
      }

      // Process glossary terms in non-link parts
      if (!isLoading && glossaryTerms) {
        let processedPart = part;
        // Sort terms by length (longest first) to match longest phrases first
        const sortedTerms = Object.entries(glossaryTerms)
          .sort(([a], [b]) => b.length - a.length);
          
        sortedTerms.forEach(([term, info]) => {
          // Escape special regex characters in the term, including parentheses
          const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          const regex = new RegExp(`(?<=^|\\s)(${escapedTerm})(?=[\\s.,;:!?]|$)`, 'gi');
          processedPart = processedPart.replace(regex, (match) => (
            `<GlossaryTerm term="${match.trim()}" definition="${info.definition.replace(/\n/g, '\\n')}" type="${info.type}" formula="${info.formula || ''}" />`
          ));
        });
        
        // Convert glossary terms to React elements
        if (processedPart.includes('<GlossaryTerm')) {
          return processedPart.split(/(<GlossaryTerm.*?\/?>)/).map((subPart, subIndex) => {
            if (subPart.startsWith('<GlossaryTerm')) {
              const termMatch = subPart.match(/term="(.*?)"/);
              const defMatch = subPart.match(/definition="(.*?)"/);
              const typeMatch = subPart.match(/type="(.*?)"/);
              const formulaMatch = subPart.match(/formula="(.*?)"/);
              return (
                <GlossaryTerm
                  key={`${index}-${subIndex}`}
                  term={termMatch ? termMatch[1] : ''}
                  definition={defMatch ? defMatch[1] : ''}
                  type={typeMatch ? typeMatch[1] : ''}
                  formula={formulaMatch ? formulaMatch[1] : null}
                />
              );
            }
            return subPart;
          });
        }
        return processedPart;
      }
      return part;
    });
  };

  const processMarkdownText = (text) => {
    if (!text) return "";
    
    let processedText = text.replace(/(go to (\d+))/gi, (match, fullMatch, sceneId) => {
      return `[${fullMatch}](#scene-${sceneId})`;
    });
    
    if (!isLoading && glossaryTerms && Object.keys(glossaryTerms).length > 0) {
      const sortedTerms = Object.entries(glossaryTerms)
        .sort(([a], [b]) => b.length - a.length);
        
      sortedTerms.forEach(([term, info]) => {
        const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        // Modified regex to handle word boundaries better - includes punctuation
        const regex = new RegExp(`(?<=^|\\s)(${escapedTerm})(?=[\\s.,;:!?]|$)`, 'gi');

        processedText = processedText.replace(regex, (match) => {
 
          return `__GLOSSARY:${match.trim()}__`;
        });

      });
    }
    return processedText;
  };

  return (
    <div className="text-panel paper-wrapper">
      <div id="story-container" className="story-container paper">
        <div className="paper-container">
          <div className="paper-content">
            {currentScene && (
              <Text h2>{currentScene.sceneId}</Text>
            )}
            <div
              id="story-text"
              className="story-text"
              style={{ 
                fontSize: `${fontSize}px`,
                columnCount: `${isTwoColumns ? 2 : 1}` 
              }}
            >
              {currentScene && currentScene.text
                .split("\n")
                .map((line, index) => {
                  if (!line.trim()) return <br key={index} />;
                  
         
                  const sanitizedLine = sanitizeMarkdown(line);
                  const shouldRenderAsMarkdown = hasMarkdown(sanitizedLine);
                  
                  if (shouldRenderAsMarkdown) {
                    // Process markdown text to a string and use custom components
                    return (
                      <div key={index} className="markdown-paragraph">
                        <ReactMarkdown 
                          remarkPlugins={[remarkGfm]} 
                          rehypePlugins={[rehypeRaw, rehypeSanitize]}
                          components={{
                            a: ({node, ...props}) => {
                              if (props.href && props.href.startsWith('#scene-')) {
                                const sceneId = props.href.replace('#scene-', '');
                                return (
                                  <a 
                                    {...props}
                                    href="#" 
                                    className="scene-link"
                                    onClick={e => handleLinkClick(e, sceneId)}
                                  />
                                );
                              }
                              return <a {...props} />;
                            },
                            text: ({node, ...props}) => {
                              const text = props.children;
                              if (typeof text === 'string' && text.includes('__GLOSSARY:')) {
                                return text.split(/(__GLOSSARY:[^_]+__)/).map((part, idx) => {
                                  if (part.startsWith('__GLOSSARY:')) {
                                    const term = part.replace('__GLOSSARY:', '').replace('__', '');
                                    const info = Object.entries(glossaryTerms).find(([key]) => 
                                      key.toLowerCase() === term.toLowerCase()
                                    );
                                    
                                    if (info) {
                                      return (
                                        <GlossaryTerm
                                          key={idx}
                                          term={term}
                                          definition={info[1].definition}
                                          type={info[1].type}
                                          formula={info[1].formula || null}
                                        />
                                      );
                                    }
                                  }
                                  return part;
                                });
                              }
                              return text;
                            }
                          }}
                        >
                          {processMarkdownText(sanitizedLine)}
                        </ReactMarkdown>
                      </div>
                    );
                  } else {
                    return <Text p key={index}>{renderTextWithLinksAndMarkdown(line)}</Text>;
                  }
                })}
            </div>
            <div id="story-options" className="story-options">
              {currentScene && currentScene.options &&
                currentScene.options.map((option, index) => (
                  <div key={index}>
                    <a
                      href={`#scene-${option.sceneId}`}
                      className="scene-link"
                      onClick={e => {
                        e.preventDefault();
                        handleLinkClick(e, option.sceneId);
                      }}
                    >
                      <Text span>{option.text}</Text>
                    </a>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoryContainer;
